import { useEffect, useMemo, useRef, useState } from "react";
import { Box, LinearProgress, useTheme } from "@mui/material";
import { Spinner } from "../ui/Spinner";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";

export type ListColumn<T> = {
  key: string;
  header: string;
  render: (row: T) => React.ReactNode;
  sortAccessor?: (row: T) => string | number | null;
  align?: "left" | "right" | "center";
  width?: string;
};

export type ListFilterField =
  | {
      kind: "text";
      key: string;
      label: string;
      value: string;
      onChange: (value: string) => void;
      placeholder?: string;
    }
  | {
      kind: "select";
      key: string;
      label: string;
      value: string;
      onChange: (value: string) => void;
      options: { value: string; label: string }[];
    }
  | {
      kind: "autocomplete";
      key: string;
      label: string;
      value: string;
      onChange: (value: string) => void;
      options: { value: string; label: string }[];
      placeholder?: string;
      disabled?: boolean;
    }
  | {
      kind: "date";
      key: string;
      label: string;
      value: string;
      onChange: (value: string) => void;
    };

export type ListSidePanel = {
  title: string;
  items: { id: string | number; label: string }[];
  selectedId: string | number | null;
  onSelect: (id: string | number | null) => void;
  loading?: boolean;
  searchPlaceholder?: string;
};

export type ListTabs = {
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
};

interface Props<T> {
  title: string;
  subtitle?: string;
  columns: ListColumn<T>[];
  rows: T[];
  getRowId: (row: T) => string | number;
  rowsLoading: boolean;
  isError?: boolean;
  errorMessage?: string;
  emptyTitle?: string;
  emptySubtitle?: string;

  searchValue: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder?: string;

  filters?: ListFilterField[];

  sortBy?: string | null;
  sortDir?: "asc" | "desc";
  onSortChange?: (key: string) => void;

  page: number;
  pageSize: number;
  totalCount: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  rowsPerPageOptions?: number[];

  rowActions?: (row: T) => React.ReactNode;
  rowStatus?: (row: T) => React.ReactNode;
  toolbarActions?: React.ReactNode;
  tabs?: ListTabs;
  sidePanel?: ListSidePanel;
}

export function ListPageLayout<T>({
  title,
  subtitle,
  columns,
  rows,
  getRowId,
  rowsLoading,
  isError = false,
  errorMessage = "Something went wrong. Please try again.",
  emptyTitle,
  emptySubtitle = "Try clearing a filter.",
  searchValue,
  onSearchChange,
  searchPlaceholder,
  filters = [],
  sortBy: controlledSortBy,
  sortDir: controlledSortDir = "asc",
  onSortChange,
  page,
  pageSize,
  totalCount,
  onPageChange,
  onPageSizeChange,
  rowsPerPageOptions = [10, 25, 50, 100],
  rowActions,
  rowStatus,
  toolbarActions,
  tabs,
  sidePanel,
}: Props<T>) {
  const theme = useTheme();
  const accent = theme.palette.secondary.main;

  const filterInputStyle: React.CSSProperties = useMemo(
    () => ({ ...baseFilterInputStyle, border: `1px solid ${theme.palette.divider}` }),
    [theme.palette.divider],
  );

  const [searchDraft, setSearchDraft] = useState(searchValue);
  const debouncedSearch = useDebouncedValue(searchDraft, 300);
  const isFirstRun = useRef(true);
  const isSearchEffectFirstRun = useRef(true);

  // Ruling 2b: keep searchDraft in sync whenever the searchValue prop changes
  // externally (e.g. a parent resetting search), without clobbering in-progress
  // typing on every render — this effect only fires when searchValue itself changes.
  useEffect(() => {
    setSearchDraft(searchValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue]);

  // Skip the mount invocation: on first render debouncedSearch already equals the
  // initial searchValue prop (useDebouncedValue's useState seeds with no delay), so
  // firing onSearchChange here would be a redundant, synchronous mount call with the
  // parent's own current value — it should only fire on genuine post-mount changes.
  useEffect(() => {
    if (isSearchEffectFirstRun.current) {
      isSearchEffectFirstRun.current = false;
      return;
    }
    onSearchChange(debouncedSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  // Internal side-panel search: local to ListPageLayout, not lifted to the parent
  // (the parent already owns the full sidePanel.items list). Mirrors
  // DocumentsPortalLayout's wellSearch behavior — plain local state, no debounce,
  // no auto-clear when sidePanel.items changes.
  const [sidePanelSearch, setSidePanelSearch] = useState("");

  const [localSortBy, setLocalSortBy] = useState<string | null>(controlledSortBy ?? null);
  const [localSortDir, setLocalSortDir] = useState<"asc" | "desc">(controlledSortDir);
  const sortBy = onSortChange ? controlledSortBy ?? null : localSortBy;
  const sortDir = onSortChange ? controlledSortDir : localSortDir;

  const filterSignature = JSON.stringify(filters.map((f) => f.value));
  useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false;
      return;
    }
    // Only reset when we are not already on the first page. Consumers implement
    // onPageChange as `setPaginationModel({ ...paginationModel, page })`, which
    // mints a new object even when the page number is unchanged — and that new
    // identity triggers a SECOND useFetch run on top of the one the filter/search
    // value change already caused. Guarding here makes it one fetch per change.
    if (page !== 0) onPageChange(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, filterSignature, sortBy, sortDir]);

  const handleSort = (col: ListColumn<T>) => {
    if (!col.sortAccessor) return;
    const nextDir = sortBy === col.key && sortDir === "asc" ? "desc" : "asc";
    if (onSortChange) {
      onSortChange(col.key);
      return;
    }
    setLocalSortBy(col.key);
    setLocalSortDir(nextDir);
  };

  const displayedRows = useMemo(() => {
    if (onSortChange || !sortBy) return rows;
    const col = columns.find((c) => c.key === sortBy);
    if (!col?.sortAccessor) return rows;
    const sorted = [...rows].sort((a, b) => {
      const av = col.sortAccessor!(a);
      const bv = col.sortAccessor!(b);
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [rows, sortBy, sortDir, columns, onSortChange]);

  const filteredSidePanelItems = useMemo(() => {
    if (!sidePanel) return [];
    const q = sidePanelSearch.trim().toLowerCase();
    return q ? sidePanel.items.filter((item) => item.label.toLowerCase().includes(q)) : sidePanel.items;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sidePanel?.items, sidePanelSearch]);

  const hasActiveFilters = filters.some((f) => Boolean(f.value)) || Boolean(searchValue);
  const clearAllFilters = () => {
    setSearchDraft("");
    filters.forEach((f) => f.onChange(""));
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const pageStart = totalCount === 0 ? 0 : page * pageSize + 1;
  const pageEnd = Math.min((page + 1) * pageSize, totalCount);

  const gridCols = [
    ...columns.map((c) => c.width ?? "1fr"),
    ...(rowStatus ? ["160px"] : []),
    ...(rowActions ? ["auto"] : []),
  ].join(" ");

  const renderFilterControl = (field: ListFilterField) => {
    switch (field.kind) {
      case "text":
        return (
          <input
            id={`list-filter-${field.key}`}
            value={field.value}
            onChange={(e) => field.onChange(e.target.value)}
            placeholder={field.placeholder}
            style={filterInputStyle}
          />
        );
      case "date":
        return (
          <input
            id={`list-filter-${field.key}`}
            type="date"
            value={field.value}
            onChange={(e) => field.onChange(e.target.value)}
            style={filterInputStyle}
          />
        );
      case "select":
      case "autocomplete":
        return (
          <select
            id={`list-filter-${field.key}`}
            value={field.value}
            disabled={field.kind === "autocomplete" ? field.disabled : undefined}
            onChange={(e) => field.onChange(e.target.value)}
            style={filterInputStyle}
          >
            <option value="">
              {field.kind === "autocomplete" ? field.placeholder ?? "All" : "All"}
            </option>
            {field.options.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        );
      default:
        return null;
    }
  };

  // Every call site supplies a `label`; render it above the control the way the
  // pre-migration DocumentsPortalLayout did (small uppercase caption).
  const renderFilterField = (field: ListFilterField) => (
    <Box key={field.key} sx={{ display: "flex", flexDirection: "column" }}>
      <Box component="label" htmlFor={`list-filter-${field.key}`} sx={filterLabelSx}>
        {field.label}
      </Box>
      {renderFilterControl(field)}
    </Box>
  );

  if (sidePanel?.loading && sidePanel.items.length === 0) return <Spinner />;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "16px", p: "20px" }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
        <Box>
          <Box sx={{ fontSize: 20, fontWeight: 700 }}>{title}</Box>
          {subtitle && <Box sx={{ fontSize: 13, color: "text.secondary" }}>{subtitle}</Box>}
        </Box>
        {toolbarActions}
      </Box>

      {tabs && (
        <Box sx={{ display: "flex", gap: "4px", borderBottom: `1px solid ${theme.palette.divider}` }}>
          {tabs.options.map((opt) => (
            <Box
              key={opt.value}
              component="button"
              onClick={() => tabs.onChange(opt.value)}
              sx={{
                border: "none",
                background: "transparent",
                borderBottom: tabs.value === opt.value ? `2px solid ${accent}` : "2px solid transparent",
                color: tabs.value === opt.value ? accent : "text.secondary",
                fontWeight: tabs.value === opt.value ? 700 : 500,
                fontSize: 13,
                py: "10px",
                px: "14px",
                cursor: "pointer",
              }}
            >
              {opt.label}
            </Box>
          ))}
        </Box>
      )}

      <Box sx={{ display: "grid", gridTemplateColumns: sidePanel ? "280px 1fr" : "1fr", gap: "16px" }}>
        {sidePanel && (
          <Box sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: "12px", overflow: "hidden" }}>
            <Box sx={{ p: "12px 14px", fontSize: 12, fontWeight: 700, textTransform: "uppercase" }}>
              {sidePanel.title}
            </Box>
            <Box sx={{ p: "0 14px 10px" }}>
              <input
                value={sidePanelSearch}
                onChange={(e) => setSidePanelSearch(e.target.value)}
                placeholder={sidePanel.searchPlaceholder ?? "Search…"}
                style={{ ...filterInputStyle, width: "100%", boxSizing: "border-box" }}
              />
            </Box>
            <Box sx={{ maxHeight: 420, overflowY: "auto" }}>
              {filteredSidePanelItems.map((item) => (
                <Box
                  key={item.id}
                  onClick={() => sidePanel.onSelect(sidePanel.selectedId === item.id ? null : item.id)}
                  sx={{
                    p: "10px 14px",
                    cursor: "pointer",
                    fontSize: 13.5,
                    fontWeight: sidePanel.selectedId === item.id ? 700 : 500,
                    color: sidePanel.selectedId === item.id ? accent : "text.primary",
                    borderBottom: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  {item.label}
                </Box>
              ))}
            </Box>
          </Box>
        )}

        <Box sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: "12px", overflow: "hidden" }}>
          <Box sx={{ p: "14px 16px", display: "flex", gap: "11px", flexWrap: "wrap", alignItems: "flex-end", borderBottom: `1px solid ${theme.palette.divider}` }}>
            <input
              value={searchDraft}
              onChange={(e) => setSearchDraft(e.target.value)}
              placeholder={searchPlaceholder ?? `Search ${title.toLowerCase()}…`}
              style={{ ...filterInputStyle, flex: "1 1 220px" }}
            />
            {filters.map(renderFilterField)}
            {hasActiveFilters && (
              <Box component="button" onClick={clearAllFilters} sx={clearButtonSx}>
                Clear filters
              </Box>
            )}
          </Box>

          {/* Refetch indicator: shown whenever rowsLoading is true, regardless of
              whether rows are already on screen — additive to (not a replacement
              for) the full-blank spinner below for the true first-load case
              (rowsLoading && rows.length === 0). Keeps stale rows visible during a
              search/filter/sort-triggered refetch instead of leaving no indication
              anything is happening. */}
          {rowsLoading && (
            <Box sx={{ height: 3, overflow: "hidden" }}>
              <LinearProgress sx={{ height: 3 }} />
            </Box>
          )}

          <Box
            role="row"
            sx={{ display: "grid", gridTemplateColumns: gridCols, p: "10px 16px", fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: "text.secondary", borderBottom: `1px solid ${theme.palette.divider}` }}
          >
            {columns.map((col) => (
              <Box
                key={col.key}
                onClick={() => handleSort(col)}
                sx={{
                  cursor: col.sortAccessor ? "pointer" : "default",
                  textAlign: col.align ?? "left",
                  userSelect: "none",
                }}
              >
                {col.header}
                {sortBy === col.key ? (sortDir === "asc" ? " ▲" : " ▼") : ""}
              </Box>
            ))}
            {rowStatus && <Box>Status</Box>}
            {rowActions && <Box sx={{ textAlign: "right" }}>Actions</Box>}
          </Box>

          {rowsLoading && rows.length === 0 ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <Spinner />
            </Box>
          ) : (
            displayedRows.map((row) => (
              <Box
                key={getRowId(row)}
                role="row"
                sx={{ display: "grid", gridTemplateColumns: gridCols, p: "10px 16px", fontSize: 13.5, borderBottom: `1px solid ${theme.palette.divider}` }}
              >
                {columns.map((col) => (
                  <Box key={col.key} sx={{ textAlign: col.align ?? "left" }}>
                    {col.render(row)}
                  </Box>
                ))}
                {rowStatus && <Box>{rowStatus(row)}</Box>}
                {rowActions && <Box sx={{ display: "flex", justifyContent: "flex-end", gap: "4px" }}>{rowActions(row)}</Box>}
              </Box>
            ))
          )}

          {totalCount === 0 && !rowsLoading && (
            <Box sx={{ p: "48px 20px", textAlign: "center", color: "text.secondary" }}>
              <Box sx={{ fontSize: 14, fontWeight: 600 }}>
                {emptyTitle ?? `No ${title.toLowerCase()} match your filters`}
              </Box>
              <Box sx={{ fontSize: 13, mt: "4px" }}>{emptySubtitle}</Box>
            </Box>
          )}

          <Box sx={{ display: "flex", alignItems: "center", gap: "16px", p: "10px 16px", fontSize: 12.5, color: "text.secondary", borderTop: `1px solid ${theme.palette.divider}` }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span>Rows per page</span>
              <select
                value={pageSize}
                onChange={(e) => onPageSizeChange(parseInt(e.target.value, 10))}
                style={{ padding: "4px 6px" }}
              >
                {rowsPerPageOptions.map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </Box>
            <Box sx={{ ml: "auto" }}>{pageStart}–{pageEnd} of {totalCount}</Box>
            <Box sx={{ display: "flex", gap: "6px" }}>
              <button disabled={page <= 0} onClick={() => onPageChange(page - 1)}>‹</button>
              <button disabled={page >= totalPages - 1} onClick={() => onPageChange(page + 1)}>›</button>
            </Box>
          </Box>
        </Box>
      </Box>

      {isError && (
        <Box sx={{ position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)", background: theme.palette.error.main, color: theme.palette.error.contrastText, p: "10px 18px", borderRadius: "9px", fontSize: 13, zIndex: 1400 }}>
          {errorMessage}
        </Box>
      )}
    </Box>
  );
}

const baseFilterInputStyle: React.CSSProperties = {
  padding: "8px 11px",
  borderRadius: 9,
  fontSize: 13,
  outline: "none",
  fontFamily: "inherit",
  minWidth: 140,
};

const filterLabelSx = {
  fontSize: 10.5,
  fontWeight: 600,
  letterSpacing: ".6px",
  textTransform: "uppercase",
  color: "text.secondary",
  display: "block",
  mb: "5px",
};

const clearButtonSx = {
  p: "8px 13px",
  border: "1px solid",
  borderColor: "divider",
  borderRadius: "9px",
  fontSize: 12.5,
  fontWeight: 600,
  cursor: "pointer",
  background: "transparent",
  color: "text.secondary",
  fontFamily: "inherit",
};
